import { useState } from "react";
import { Plus, Minus } from "lucide-react";
import SEO from "@/components/SEO";

const FAQS = [
  {
    q: "Faut-il une autorisation pour installer un habitat YONO ?",
    a: "Oui, dans la plupart des cas une autorisation d'urbanisme peut être nécessaire. Cela dépend de la commune, du terrain, de la surface, du PLU et de l'usage prévu. Avant tout projet, nous vous aidons à clarifier les démarches à prévoir auprès de votre mairie.",
  },
  {
    q: "Où peut-on installer un habitat YONO ?",
    a: "Un habitat YONO peut être installé dans un jardin, sur un terrain privé, dans un domaine d'accueil ou sur un site destiné à la location courte durée, sous réserve des règles d'urbanisme locales et des conditions d'accès au terrain.",
  },
  {
    q: "Le terrain doit-il être préparé avant la livraison ?",
    a: "Oui. Le terrain doit être accessible, stable et prêt à recevoir l'habitat. Selon le projet, une préparation simple peut être nécessaire : nivellement, points d'appui, arrivées d'eau, électricité et évacuation des eaux usées.",
  },
  {
    q: "Faut-il faire des fondations ?",
    a: "Les habitats YONO sont conçus pour limiter les travaux lourds. Selon la nature du sol, ils peuvent être posés sur des appuis adaptés ou des pieds métalliques. La solution exacte est définie selon le terrain et les contraintes du projet.",
  },
  {
    q: "L'habitat est-il livré prêt à vivre ?",
    a: "Oui, les habitats YONO sont conçus pour être livrés équipés et prêts à raccorder. Ils intègrent les principaux éléments d'aménagement selon le modèle choisi : espace de vie, couchages, kitchenette, salle d'eau et équipements techniques.",
  },
  {
    q: "Que signifie « prêt à raccorder » ?",
    a: "Cela signifie que l'habitat est préparé en atelier pour être raccordé sur site aux réseaux nécessaires : électricité, eau, évacuation des eaux usées. Les raccordements dépendent toutefois de la configuration du terrain et sont étudiés au cas par cas.",
  },
  {
    q: "Peut-on déplacer un habitat YONO ?",
    a: "Oui, les habitats YONO sont pensés comme des volumes compacts et déplaçables. Ils peuvent être transportés et repositionnés, sous réserve de respecter les contraintes de manutention, d'accès au terrain et de raccordement.",
  },
  {
    q: "Combien de temps dure l'installation ?",
    a: "L'installation sur site est rapide, une fois le terrain préparé et les accès validés. La durée exacte dépend du modèle, de la configuration du terrain, de la manutention et des raccordements à réaliser.",
  },
  {
    q: "Les habitats YONO sont-ils utilisables toute l'année ?",
    a: "Oui, les habitats YONO sont conçus pour offrir un bon niveau de confort en toutes saisons, avec une structure bois, une isolation adaptée, une ventilation et des équipements de chauffage selon configuration.",
  },
  {
    q: "Quelle est la différence avec un simple studio de jardin ?",
    a: "YONO n'est pas une simple annexe. Chaque habitat est pensé comme une petite architecture compacte : proportions, lumière, matériaux, finitions, confort intérieur et qualité de fabrication sont travaillés pour créer un vrai lieu de vie.",
  },
  {
    q: "Peut-on personnaliser son habitat YONO ?",
    a: "Oui, plusieurs options sont possibles selon les modèles : terrasse, auvent, climatisation réversible, pack literie, mobilier complémentaire, finitions ou équipements spécifiques. Les possibilités sont étudiées selon votre projet.",
  },
  {
    q: "Peut-on utiliser un habitat YONO pour de la location courte durée ?",
    a: "Oui, c'est l'un des usages possibles. Un habitat YONO peut permettre de valoriser un terrain ou un jardin avec un hébergement compact et différenciant. La rentabilité dépend de l'emplacement, de la demande locale, du prix par nuit, de la saisonnalité et de la qualité d'exploitation.",
  },
  {
    q: "Comment obtenir un prix pour mon projet ?",
    a: "Nos prix sont communiqués sur demande, via un devis personnalisé. Il est établi selon le modèle choisi, les options retenues, la livraison, les raccordements, la préparation du terrain et les éventuelles démarches administratives propres à chaque projet.",
  },
  {
    q: "Comment démarrer un projet YONO ?",
    a: "Il suffit de nous présenter votre besoin, votre terrain, votre commune et l'usage envisagé. Nous vous aidons ensuite à identifier le modèle adapté, les contraintes techniques, les démarches à prévoir et l'enveloppe budgétaire du projet.",
  },
];

// JSON-LD for SEO (Google FAQ rich results)
const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
};

export default function FAQPage() {
  const [open, setOpen] = useState(null);

  return (
    <div data-testid="faq-page" className="bg-off-white">
      <SEO
        title="FAQ — Questions fréquentes"
        description="Toutes les réponses sur l'installation, la livraison, les autorisations, la personnalisation et l'usage des habitats bois YONO."
        path="/faq"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      {/* Header */}
      <section className="pt-32 pb-12 md:pt-40 md:pb-16 px-6 md:px-12 lg:px-24 border-b border-warm-grey/30">
        <div className="max-w-4xl mx-auto">
          <span className="font-mono text-sm uppercase tracking-[0.15em] text-soft-black/40">Questions fréquentes</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-soft-black mt-4 mb-6">
            Tout ce qu'il faut savoir avant&nbsp;de&nbsp;commander
          </h1>
          <p className="font-body text-base text-soft-black/60 leading-relaxed max-w-2xl">
            Autorisations, installation, raccordements, personnalisation : retrouvez ici les réponses aux questions
            que se posent le plus souvent nos clients sur les habitats YONO.
          </p>
        </div>
      </section>

      {/* FAQ items */}
      <section className="py-12 md:py-20 px-6 md:px-12 lg:px-24">
        <div className="max-w-3xl mx-auto">
          <div className="divide-y divide-warm-grey/30 border-y border-warm-grey/30">
            {FAQS.map(({ q, a }) => {
              const isOpen = open === q;
              return (
                <div key={q} data-testid={`faq-item-${q.slice(0, 30)}`}>
                  <button
                    onClick={() => setOpen(isOpen ? null : q)}
                    className="w-full flex items-start justify-between gap-6 py-6 text-left group"
                    aria-expanded={isOpen}
                  >
                    <span className="font-heading text-lg md:text-xl tracking-tight text-soft-black group-hover:text-natural-wood transition-colors">
                      {q}
                    </span>
                    <span className="flex-shrink-0 mt-1">
                      {isOpen ? (
                        <Minus className="h-5 w-5 text-soft-black/60" strokeWidth={1.5} />
                      ) : (
                        <Plus className="h-5 w-5 text-soft-black/60" strokeWidth={1.5} />
                      )}
                    </span>
                  </button>
                  <div
                    className={`grid transition-all duration-300 ease-out ${
                      isOpen ? "grid-rows-[1fr] opacity-100 pb-6" : "grid-rows-[0fr] opacity-0"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <p className="font-body text-base text-soft-black/70 leading-relaxed pr-12">{a}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-14 md:py-20 px-6 md:px-12 lg:px-24 bg-light-beige border-t border-warm-grey/30">
        <div className="max-w-2xl mx-auto text-center">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-soft-black/40">Une autre question ?</span>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-soft-black mt-3 mb-6">
            Nous étudions chaque projet au cas par cas
          </h2>
          <p className="font-body text-base text-soft-black/60 leading-relaxed mb-8">
            Présentez-nous votre terrain, votre commune et votre usage : nous vous accompagnons sur les démarches,
            le modèle adapté et l'enveloppe budgétaire.
          </p>
          <a
            href="/contact"
            data-testid="faq-contact-cta"
            className="inline-flex items-center gap-2 bg-soft-black text-off-white px-8 py-4 font-body text-xs uppercase tracking-[0.15em] hover:bg-soft-black/90 transition-colors"
          >
            Nous contacter
          </a>
        </div>
      </section>
    </div>
  );
}
